import Cookies from 'js-cookie';
import isPlainObject from 'lodash/isPlainObject';
import {getCrossSubdomainCookieDomain} from './cookieHelpers';

/**
 * Sole owner of the cross-booking `tripHighlights` cookie contract, shared by the
 * hotel step and the ticket-category component. The cookie records, per
 * arrangement, the item the user picked in a *prior* booking/visit.
 *
 * Schema: `{ [arrangementId]: { ticket?: number, hotel?: string } }`. Missing or
 * malformed values resolve to `null`; writes are best-effort (see below).
 */

const TRIP_HIGHLIGHTS_COOKIE = 'tripHighlights',

      COOKIE_OPTIONS = {
	      'expires' : 365,
	      'path'    : '/',
	      'sameSite': 'Lax'
      },

      /**
       * @returns {Record<string, Record<string, unknown>>}
       */
      readTripHighlights = () => {
	      const rawValue = Cookies.get(TRIP_HIGHLIGHTS_COOKIE);

	      if (!rawValue) {
		      return {};
	      }

	      try {
		      const parsed = JSON.parse(rawValue);

		      return isPlainObject(parsed) ? parsed : {};
	      } catch {
		      return {};
	      }
      },

      /**
       * @param   {string} arrangementId
       * @returns {Record<string, unknown>}
       */
      readArrangementEntry = (arrangementId) => {
	      const entry = readTripHighlights()[arrangementId];

	      return isPlainObject(entry) ? entry : {};
      },

      /**
       * Upsert `value` under `arrangementId[key]`, preserving every other key and
       * arrangement. Best-effort: a corrupt cookie or a `Cookies.set` failure is
       * swallowed so persistence can never interrupt a checkout in progress.
       *
       * @param {string}  arrangementId
       * @param {string}  key
       * @param {unknown} value
       */
      writeTripHighlight = (arrangementId, key, value) => {
	      try {
		      const tripHighlights = readTripHighlights(),
		            entry          = isPlainObject(tripHighlights[arrangementId])
			            ? tripHighlights[arrangementId]
			            : {};

		      if (entry[key] === value) {
			      return;
		      }

		      entry[key]                    = value;
		      tripHighlights[arrangementId] = entry;

		      Cookies.set(
			      TRIP_HIGHLIGHTS_COOKIE,
			      JSON.stringify(tripHighlights),
			      {...COOKIE_OPTIONS, 'domain': getCrossSubdomainCookieDomain()}
		      );
	      } catch {
		      // best-effort — see the contract above
	      }
      },

      /**
       * The hotel id (result card `id` / server `hotel_search_id`) selected for
       * this arrangement in a prior booking, or `null`.
       *
       * @param   {string} arrangementId
       * @returns {string | null}
       */
      getPreviouslySelectedHotelId = (arrangementId) => {
	      const {hotel} = readArrangementEntry(arrangementId);

	      return typeof hotel === 'string' ? hotel : null;
      },

      /**
       * Best-effort persist of the selected hotel id. No-ops on a missing hotel id.
       *
       * @param {string}                    arrangementId
       * @param {string | null | undefined} hotelId
       */
      savePreviouslySelectedHotelId = (arrangementId, hotelId) => {
	      if (!hotelId) {
		      return;
	      }

	      writeTripHighlight(arrangementId, 'hotel', hotelId);
      },

      /**
       * The ticket-category id selected for this arrangement in a prior booking,
       * or `null`.
       *
       * @param   {string} arrangementId
       * @returns {number | null}
       */
      getPreviouslySelectedTicketCategoryId = (arrangementId) => {
	      const {ticket} = readArrangementEntry(arrangementId);

	      return typeof ticket === 'number' ? ticket : null;
      },

      /**
       * Best-effort persist of the selected ticket-category id.
       *
       * @param {string} arrangementId
       * @param {number} ticketCategoryId
       */
      savePreviouslySelectedTicketCategoryId = (arrangementId, ticketCategoryId) => {
	      writeTripHighlight(arrangementId, 'ticket', ticketCategoryId);
      };

export {
	getPreviouslySelectedHotelId,
	savePreviouslySelectedHotelId,
	getPreviouslySelectedTicketCategoryId,
	savePreviouslySelectedTicketCategoryId
};